import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Send } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

export default function TrainingAssign() {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [kind, setKind] = useState("all");
  const [campusId, setCampusId] = useState("");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/training/trainings/${trainingId}`).then((r) => {
      setTraining(r.data);
      setDueDate((r.data.due_date || "").split("T")[0]);
    }).catch(() => {});
    api.get("/users/directory").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/campuses").then((r) => setCampuses(r.data)).catch(() => {});
  }, [trainingId]);

  const departments = useMemo(() => {
    const s = new Set();
    users.forEach((u) => u.department && s.add(u.department));
    return Array.from(s);
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(
      (u) =>
        !q ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q),
    );
  }, [users, search]);

  const toggle = (id) => {
    setSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );
  };

  const assign = async () => {
    setBusy(true);
    try {
      const body = {
        kind,
        campus_id: (kind === "campus" || kind === "volunteers_by_campus") ? campusId : null,
        role: kind === "role" ? role : null,
        department: kind === "department" ? department : null,
        user_ids: kind === "individuals" ? selectedIds : null,
        due_date: dueDate || null,
      };
      const { data } = await api.post(`/training/trainings/${trainingId}/assign`, body);
      toast.success(`Assigned to ${data.assigned} new teammate${data.assigned === 1 ? "" : "s"} (audience ${data.audience_size}).`);
      navigate("/training/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!training) {
    return (
      <div className="patria-card p-8">
        <p className="text-charcoal-muted italic">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-assign-page">
      <Link
        to="/training/admin"
        className="inline-flex items-center text-forest-700 text-sm font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back
      </Link>

      <div>
        <ModuleTitleTRAINING />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Assign Audience</h1>
        <p className="mt-2 text-charcoal-soft">
          Assign <b className="text-forest-900">{training.title}</b> to staff or volunteers — everyone, by campus,
          by role, by department, hand-picked, or the whole volunteer roster.
          {training.visible_to_volunteers ? null : (
            <span className="block mt-2 text-xs italic text-terracotta-700">
              Tip: flag this training as "Visible To Volunteers" in the edit page before assigning it to volunteer audiences.
            </span>
          )}
        </p>
      </div>

      <section className="patria-card p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="patria-label">Audience *</label>
            <select
              className="patria-input mt-2 w-full"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="training-assign-kind"
            >
              <option value="all">All active staff</option>
              <option value="campus">By campus</option>
              <option value="role">By role</option>
              <option value="department">By department</option>
              <option value="individuals">Hand-picked individuals</option>
              <option value="all_volunteers">All volunteers</option>
              <option value="volunteers_by_campus">Volunteers by campus</option>
            </select>
          </div>
          <div>
            <label className="patria-label">Due date</label>
            <input
              type="date"
              className="patria-input mt-2 w-full"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="training-assign-due"
            />
          </div>
        </div>

        {kind === "campus" && (
          <div>
            <label className="patria-label">Campus</label>
            <select
              className="patria-input mt-2 w-full"
              value={campusId}
              onChange={(e) => setCampusId(e.target.value)}
              data-testid="training-assign-campus"
            >
              <option value="">Choose a campus…</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
        {kind === "volunteers_by_campus" && (
          <div>
            <label className="patria-label">Campus</label>
            <select
              className="patria-input mt-2 w-full"
              value={campusId}
              onChange={(e) => setCampusId(e.target.value)}
              data-testid="training-assign-vol-campus"
            >
              <option value="">Choose a campus…</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs italic text-charcoal-muted">
              Assigns to all active volunteers rostered at this campus.
            </p>
          </div>
        )}
        {kind === "role" && (
          <div>
            <label className="patria-label">Role</label>
            <select
              className="patria-input mt-2 w-full"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="">Choose a role…</option>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="po_admin">PO Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
        )}
        {kind === "department" && (
          <div>
            <label className="patria-label">Department</label>
            <select
              className="patria-input mt-2 w-full"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            >
              <option value="">Choose a department…</option>
              {departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        )}
        {kind === "individuals" && (
          <div>
            <label className="patria-label">Pick individuals</label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted" strokeWidth={1.5} />
              <input
                className="patria-input pl-9 w-full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email"
                data-testid="training-assign-search"
              />
            </div>
            <ul className="mt-3 max-h-72 overflow-y-auto divide-y divide-sage-300/60 border border-sage-300 rounded-lg bg-cream-100">
              {filteredUsers.slice(0, 100).map((u) => (
                <li key={u.id} className="flex items-center justify-between px-3 py-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(u.id)}
                      onChange={() => toggle(u.id)}
                      data-testid={`training-assign-user-${u.id}`}
                    />
                    <span>{u.name} <span className="text-charcoal-muted">— {u.job_title || u.email}</span></span>
                  </label>
                </li>
              ))}
              {filteredUsers.length === 0 && (
                <li className="px-3 py-4 text-sm text-charcoal-muted italic">No matches.</li>
              )}
            </ul>
            <p className="mt-1 text-xs text-charcoal-muted">
              {selectedIds.length} selected
            </p>
          </div>
        )}

        <div className="pt-4 flex justify-end border-t border-sage-300/60">
          <button
            onClick={assign}
            disabled={busy}
            className="patria-btn-primary"
            data-testid="training-assign-submit"
          >
            <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {busy ? "Assigning…" : "Assign"}
          </button>
        </div>
      </section>
    </div>
  );
}
